import CTALink from "@/components/CTA/variants/CtaLink/client";
import {
  HOMEPAGE_HERO_DARK_BG,
  HOMEPAGE_HERO_LIGHT_BG,
  HOMEPAGE_HERO_TEXT_COLOR,
} from "@/components/Homepage/HomepageHero/constants";
import type { BaseHomepageHeroProps } from "@/components/Homepage/HomepageHero/types";
import HomepageHeroBackgroundImage from "@/components/Homepage/HomepageHero/components/HomepageHeroBackgroundImage";
import HomepageHeroHeading from "@/components/Homepage/HomepageHero/components/HomepageHeroHeading";
import Icon from "@/components/icons";
import Tags from "@/components/Tags";
import validateHex from "@/utils/functions/validateHex";
import ProductSearch from "@/components/ProductSearch";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";

export default function BaseHomepageHeroMarketplace({
  title,
  titleColor = HOMEPAGE_HERO_TEXT_COLOR,
  descriptionItems,
  descriptionColor = HOMEPAGE_HERO_TEXT_COLOR,
  primaryCta,
  secondaryCta,
  desktopImage,
  tabletImage,
  mobileImage,
  badges,
  testimonial,
  stat,
  eventing,
  theme = "dark",
  headingLevel = "h1",
  className = "",
  search,
  onFocus,
  onBlur,
  isCarouselSlide = false,
}: BaseHomepageHeroProps) {
  const t = useTranslations("Homepage");
  const tSearch = useTranslations("Search");
  const resolvedTitle = title ?? t("learnAnywhereWithEdx");
  const testimonialColorHex = testimonial?.textColor
    ? (validateHex(testimonial.textColor) ?? HOMEPAGE_HERO_TEXT_COLOR)
    : HOMEPAGE_HERO_TEXT_COLOR;
  const statColorHex = stat?.textColor
    ? (validateHex(stat.textColor) ?? HOMEPAGE_HERO_TEXT_COLOR)
    : HOMEPAGE_HERO_TEXT_COLOR;
  const bgFallback = !desktopImage
    ? theme === "light"
      ? HOMEPAGE_HERO_LIGHT_BG
      : HOMEPAGE_HERO_DARK_BG
    : "";
  return (
    <div
      className={cn(
        "relative py-6 sm:py-10 lg:px-20 xl:py-32 2xl:px-32",
        isCarouselSlide ? "mx-break-out" : "",
        bgFallback,
        className,
      )}
    >
      {/* Background Image */}
      {desktopImage && (
        <>
          <HomepageHeroBackgroundImage
            imageSrc={mobileImage ?? tabletImage ?? desktopImage}
            screenSize="mobile"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={tabletImage ?? desktopImage}
            screenSize="tablet"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={desktopImage}
            screenSize="desktop"
            theme={theme}
          />
        </>
      )}

      <div
        className={`container flex flex-col justify-between gap-10 xl:max-w-screen-2xl ${!testimonial && !stat ? "sm:flex-row" : "xl:flex-row"}`}
      >
        {/* Title, Description, CTAs */}
        <div className="basis-7/12">
          {badges && badges.length > 0 && (
            <div className="mb-6">
              <Tags variant="badges" tags={badges} />
            </div>
          )}
          <HomepageHeroHeading
            title={resolvedTitle}
            titleColor={titleColor}
            descriptionItems={descriptionItems}
            descriptionColor={descriptionColor}
            headingLevel={headingLevel}
          />
          <div className="flex flex-col sm:flex-row gap-4">
            {search && (
              <ProductSearch
                searchButtonColor={search?.searchButtonColor}
                searchButtonLabelColor={search?.searchButtonLabelColor}
                placeholder={search?.placeholder ?? tSearch("searchOurCourses")}
                button={search?.searchButtonLabel ?? tSearch("searchButton")}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            )}
            {primaryCta && !search && (
              <CTALink
                className="w-full sm:w-auto sm:[&_span]:whitespace-nowrap"
                type="brand"
                size="xl"
                url={primaryCta.url}
                label={primaryCta.label}
                html_id={eventing?.primaryCtaHtmlId}
                name={eventing?.primaryCtaName}
                location={eventing?.sectionLocation ?? "hero"}
              />
            )}
            {secondaryCta && !search && (
              <CTALink
                className="w-full sm:w-auto sm:[&_span]:whitespace-nowrap"
                type="brandOutline"
                size="xl"
                url={secondaryCta.url}
                label={secondaryCta.label}
                html_id={eventing?.secondaryCtaHtmlId}
                name={eventing?.secondaryCtaName}
                location={eventing?.sectionLocation ?? "hero"}
              />
            )}
          </div>
        </div>

        {/* Testimonial */}
        {testimonial && (
          <div
            className={`flex gap-1 basis-5/12 ${badges && badges.length > 0 ? "xl:mt-14" : ""}`}
            style={{ color: testimonialColorHex }}
          >
            <div className="w-14 h-14 -mt-3">
              <Icon
                type="format-quote"
                svgProps={{ width: 56, height: 56 }}
                // flip horizontally
                pathProps={{ transform: "scale(-1 1) translate(-24 0)" }}
              />
            </div>
            <div>
              <p
                className="elm-h3 font-bold"
                style={{ color: testimonialColorHex }}
              >
                {testimonial.quote}
              </p>
              <p className="elm-p5 mb-0" style={{ color: testimonialColorHex }}>
                - {testimonial.author}
              </p>
            </div>
          </div>
        )}

        {/* Stat */}
        {stat && !testimonial && (
          <div
            className={`basis-5/12 ${badges && badges.length > 0 ? "xl:mt-14" : ""}`}
            style={{ color: statColorHex }}
          >
            <div className="flex align-center gap-4 mb-4">
              {stat.icon && (
                <div
                  className="pr-4 border-r"
                  style={{ borderColor: statColorHex }}
                >
                  <Icon type={stat.icon} svgProps={{ width: 64, height: 64 }} />
                </div>
              )}
              <p
                className="elm-d2 font-extrabold italic mb-0"
                style={{ color: statColorHex }}
              >
                {stat.value}
              </p>
            </div>
            <p className="elm-h3 font-bold" style={{ color: statColorHex }}>
              {stat.label}
            </p>
            {stat.sourceName && (
              <p className="elm-p5 mb-0" style={{ color: statColorHex }}>
                -{" "}
                {stat.sourceLink ? (
                  <a
                    href={stat.sourceLink}
                    style={{ color: statColorHex }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {stat.sourceName}
                  </a>
                ) : (
                  stat.sourceName
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
